import { h } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { memo } from 'preact/compat';

import { getCurrentUrl, route } from 'preact-router';

import { useDispatch, useSelector } from 'react-redux';

import { styled } from '@mui/material/styles';

import MuiAppBar from '@mui/material/AppBar';
import LinearProgress from '@mui/material/LinearProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import MUIAvatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Backdrop from '@mui/material/Backdrop';
import Paper from '@mui/material/Paper';

import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import MenuIcon from '@mui/icons-material/Menu';

import { actions as drawerActions } from '../../store/state/drawer.js';
import { actions as errorActions } from '../../store/state/error.js';
import { actions as successActions } from '../../store/state/success.js';
import ResponsiveImage from '../responsiveImage.jsx';

import { ON_RPM, PUBLIC_FOLDER } from '../../consts.js';

import logo from '../../assets/logo.png';
import logoRunner from '../../assets/logo_white.png';

import SearchIcon from '@mui/icons-material/Search';

import styles from '../global.css';
import OnRpm from '../conditionals/onRpm.jsx';

const AppBar = styled(
  MuiAppBar,
  {}
)(({ theme }) => ({
  transition: theme.transitions.create(['background-color'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

function AvatarStyles(input) {
  const { theme } = input;
  const { palette } = theme;
  return {
    marginRight: theme.spacing(2),
    backgroundImage: `linear-gradient(147deg, ${palette.primary.light} 0%, ${palette.primary.dark} 74%)`,
  };
}

const Avatar = styled(MUIAvatar)(AvatarStyles);

function getOrigin(property) {
  return property.origin;
}

function getNetworkActivity(state) {
  return state.network > 0;
}

function getError(state) {
  return state.error;
}

function getSuccess(state) {
  return state.success;
}

function getNavigationProperty(state) {
  return state.navigation.propertyId;
}

function renderOption(props, property) {
  const { origin, icon } = property;
  return (
    <ListItem {...props}>
      <ListItemAvatar>
        <Avatar alt={origin} width={48} height={48}>
          <ResponsiveImage src={icon} alt={origin} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText>{origin}</ListItemText>
    </ListItem>
  );
}

function AutocompleteTextFieldStyles(input) {
  const { theme } = input;
  return {
    transition: theme.transitions.create(['background-color'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    '& fieldset': { border: 'none' },
  };
}

const URL_COMPOSERS = {
  OVERVIEW(property) {
    return `${PUBLIC_FOLDER}/property/${property._id}/overview`;
  },
  LCP(property) {
    return `${PUBLIC_FOLDER}/property/${property._id}/lcp`;
  },
  CLS(property) {
    return `${PUBLIC_FOLDER}/property/${property._id}/cls`;
  },
  INP(property) {
    return `${PUBLIC_FOLDER}/property/${property._id}/inp`;
  },
  ISSUES(property) {
    return `${PUBLIC_FOLDER}/property/${property._id}/issues`;
  },
  REALTIME(property) {
    return `${PUBLIC_FOLDER}/property/${property._id}/realtime`;
  },
  CRUX(property) {
    return `${PUBLIC_FOLDER}/property/${property._id}/crux/origin`;
  },
  NO_PROPERTY(_property) {
    return `${PUBLIC_FOLDER}/properties`;
  },
  SEARCH(property) {
    return `${PUBLIC_FOLDER}/property/${property._id}/search`;
  },
};

const URL_PATH_TO_TAB_VALUE_MAP = {
  overview: 'OVERVIEW',
  lcp: 'LCP',
  cls: 'CLS',
  inp: 'INP',
  issues: 'ISSUES',
  realtime: 'REALTIME',
  crux: 'CRUX',
  search: 'SEARCH',
};

function getUrlPieces(currentUrl) {
  const currentUrlPieces = currentUrl
    .substring(PUBLIC_FOLDER.length)
    .split('/');

  const [blank, property, propertyId, scope] = currentUrlPieces;

  return [blank, property, propertyId, scope];
}

function getInitiallySelectedNavTab() {
  const [_blank, _property, _propertyId, scope] = getUrlPieces(getCurrentUrl());

  if (scope in URL_PATH_TO_TAB_VALUE_MAP) {
    return URL_PATH_TO_TAB_VALUE_MAP[scope];
  } else {
    return URL_PATH_TO_TAB_VALUE_MAP.overview;
  }
}

const StyledTextField = styled(TextField)(AutocompleteTextFieldStyles);

function getArePropertiesLoading(store) {
  return store.properties.loading;
}

function getPropertiesList(store) {
  return store.properties.list;
}

function CustomPaper(props) {
  return h(Paper, {
    ...props,
    sx: {
      ...(props.sx || {}),
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  });
}

function getAppbackBackgroundColor(isFocused) {
  if (ON_RPM) {
    return isFocused ? '#e8eaed' : '#fff';
  }

  return '#000';
}

const rpmTabStyles = {
  marginLeft: 'auto',
};

const runnerTabStyles = {
  marginLeft: 'auto',
  '& .MuiTabs-indicator': {
    backgroundColor: '#FFF',
  },
  '& .MuiTab-root': {
    color: '#ccc',
  },
  '& .MuiTab-root.Mui-selected': {
    color: '#FFF',
  },
};

function HeaderTab(allProps) {
  const { property, label, value, onTabChange, ...props } = allProps;

  const href = URL_COMPOSERS[value](property);

  const onClick = useCallback(
    function onClick(_event) {
      onTabChange(null, value);
    },
    [onTabChange, value]
  );

  return h(Tab, { ...props, href, label, value, onClick });
}

function Header(_props) {
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedTab, setSelectedTab] = useState(getInitiallySelectedNavTab);
  const [isFocused, setIsFocused] = useState(false);

  const isNetworkActive = useSelector(getNetworkActivity);
  const error = useSelector(getError);
  const success = useSelector(getSuccess);
  const navigatedPropertyId = useSelector(getNavigationProperty);

  const loadingProperties = useSelector(getArePropertiesLoading);
  const properties = useSelector(getPropertiesList);

  const dispatch = useDispatch();

  const currentUrl = getCurrentUrl();

  const openDrawer = useCallback(
    function openDrawer() {
      dispatch(drawerActions.OPEN());
    },
    [dispatch]
  );

  const clearError = useCallback(
    function clearError() {
      dispatch(errorActions.CLEAR());
    },
    [dispatch]
  );

  const clearSuccess = useCallback(
    function clearSuccess() {
      dispatch(successActions.CLEAR());
    },
    [dispatch]
  );

  const onTabChange = useCallback(function onChange(_, newValue) {
    setSelectedTab(newValue);
    setShouldNavigate(true);
  }, []);

  useEffect(
    function updateTab() {
      const [_blank, _property, _propertyId, scope] = getUrlPieces(currentUrl);

      if (!(scope in URL_PATH_TO_TAB_VALUE_MAP)) {
        return;
      }

      const urlTab = URL_PATH_TO_TAB_VALUE_MAP[scope];

      if (urlTab != selectedTab) {
        setSelectedTab(urlTab);
      }
    },
    [currentUrl, selectedTab]
  );

  const onPropertyChange = useCallback(function onPropertyChange(event, value) {
    setSelectedProperty(value);
    setShouldNavigate(true);
    if (event) {
      event.preventDefault();
      return false;
    }
  }, []);

  const onAutocompleteFocus = useCallback(function onAutocompleteFocus() {
    setIsFocused(true);
  }, []);

  const onAutoCompleteBlur = useCallback(function onAutoCompleteBlur() {
    setIsFocused(false);
  }, []);

  useEffect(
    function reactPropertyToNavigation() {
      if (!navigatedPropertyId) {
        setSelectedProperty(null);
        return;
      }

      function findProp(property) {
        return property._id === navigatedPropertyId;
      }

      const foundProp = properties.find(findProp);

      if (foundProp) {
        setSelectedProperty(foundProp);
      }
    },
    [navigatedPropertyId, properties]
  );

  useEffect(
    function onPropertyChanged() {
      if (!shouldNavigate) {
        return;
      }

      setShouldNavigate(false);

      let url;

      if (selectedProperty) {
        url = URL_COMPOSERS[selectedTab](selectedProperty);
      } else {
        url = URL_COMPOSERS.NO_PROPERTY(selectedProperty);
        onAutoCompleteBlur();
      }

      route(url);
    },
    [selectedProperty, selectedTab, shouldNavigate, onAutoCompleteBlur]
  );

  useEffect(
    function onPropertyChange() {
      if (selectedProperty) {
        setSelectedTab(getInitiallySelectedNavTab());
        setShouldNavigate(false);
      }
    },
    [selectedProperty]
  );

  const renderInputForAutocomplete = useCallback(
    function renderInputForAutocomplete(params) {
      let startAdornment = null;
      if (selectedProperty) {
        const { origin, icon } = selectedProperty;
        startAdornment = (
          <Avatar alt={origin} width={24} height={24}>
            <ResponsiveImage src={icon} alt={origin} />
          </Avatar>
        );
      }

      let endAdornment = params.InputProps.endAdornment;

      if (loadingProperties) {
        endAdornment = (
          <>
            <CircularProgress color="inherit" size={20} />
            {endAdornment}
          </>
        );
      }

      return (
        <StyledTextField
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment,
            startAdornment,
          }}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password', // disable autocomplete and autofill
          }}
        />
      );
    },
    [loadingProperties, selectedProperty]
  );

  return (
    <AppBar
      position="absolute"
      sx={{ backgroundColor: getAppbackBackgroundColor(isFocused) }}
    >
      <Toolbar
        sx={{
          pr: '24px',
        }}
      >
        <IconButton
          edge="start"
          aria-label="open drawer"
          onClick={openDrawer}
          sx={{
            color: ON_RPM ? '#5f6368' : '#FFF',
            marginRight: '36px',
          }}
        >
          <MenuIcon />
        </IconButton>
        <img
          src={ON_RPM ? logo : logoRunner}
          class={styles.headerLogo}
          style={{ width: 360 }}
          alt="Rebelmouse Performance Monitoring"
        />
        <OnRpm>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              color: ON_RPM ? '#5f6368' : '#FFF',
              display: { xs: 'none', md: 'inherit' },
            }}
          />
          <Autocomplete
            value={selectedProperty}
            options={properties}
            autoHighlight
            sx={{
              width: 300,
              backgroundColor: '#fff',
              zIndex: 3,
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
            }}
            PaperComponent={CustomPaper}
            openOnFocus
            blurOnSelect={true}
            onFocus={onAutocompleteFocus}
            onBlur={onAutoCompleteBlur}
            loading={loadingProperties}
            getOptionLabel={getOrigin}
            renderOption={renderOption}
            onChange={onPropertyChange}
            renderInput={renderInputForAutocomplete}
          />
        </OnRpm>
        {selectedProperty && (
          <Tabs
            value={selectedTab}
            onChange={onTabChange}
            aria-label="Select view"
            sx={ON_RPM ? rpmTabStyles : runnerTabStyles}
          >
            <HeaderTab
              property={selectedProperty}
              label="OVERVIEW"
              value="OVERVIEW"
              onTabChange={onTabChange}
            />
            <HeaderTab
              property={selectedProperty}
              label="CRUX"
              value="CRUX"
              onTabChange={onTabChange}
            />
            <HeaderTab
              property={selectedProperty}
              label="ISSUES"
              value="ISSUES"
              onTabChange={onTabChange}
            />
            <HeaderTab
              property={selectedProperty}
              label="LCP"
              value="LCP"
              onTabChange={onTabChange}
            />
            <HeaderTab
              property={selectedProperty}
              label="CLS"
              value="CLS"
              onTabChange={onTabChange}
            />
            <HeaderTab
              property={selectedProperty}
              label="INP"
              value="INP"
              onTabChange={onTabChange}
            />
            <HeaderTab
              property={selectedProperty}
              label="Real Time"
              value="REALTIME"
              onTabChange={onTabChange}
            />
            <HeaderTab
              property={selectedProperty}
              value="SEARCH"
              icon={<SearchIcon />}
              onTabChange={onTabChange}
            />
          </Tabs>
        )}
      </Toolbar>
      {isNetworkActive && <LinearProgress color="primary" />}
      <Snackbar open={!!error} autoHideDuration={5000} onClose={clearError}>
        <Alert onClose={clearError} severity="error">
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={5000} onClose={clearSuccess}>
        <Alert onClose={clearSuccess} severity="success">
          {success}
        </Alert>
      </Snackbar>
      <Backdrop open={isFocused} />
    </AppBar>
  );
}

export default memo(Header);
